import React from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    // 更新 state 使下一次渲染能够显示降级后的 UI
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // 记录错误信息
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({
      error: error,
      errorInfo: errorInfo
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    
    // 清理可能损坏的存储数据
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('altverse-') || key.startsWith('user-edited-')) {
          try {
            const data = JSON.parse(localStorage.getItem(key))
            if (JSON.stringify(data).includes('NaN') || 
                JSON.stringify(data).includes('undefined') ||
                JSON.stringify(data).includes('[object Object]')) {
              localStorage.removeItem(key)
              console.log('清理损坏的存储数据:', key)
            }
          } catch (error) {
            localStorage.removeItem(key)
            console.log('清理无效的存储数据:', key)
          }
        }
      })
    } catch (error) {
      console.error('清理存储时出错:', error)
    }
  }

  handleClearStorage = () => {
    if (confirm('确定要清空所有存储数据吗？这将删除所有故事和设置，此操作不可恢复！')) {
      try {
        const keys = Object.keys(localStorage)
        keys.forEach(key => {
          if (key.startsWith('altverse-') || key.startsWith('user-edited-')) {
            localStorage.removeItem(key)
          }
        })
        console.log('存储数据已清空')
        this.handleReset()
      } catch (error) {
        console.error('清空存储时出错:', error)
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-dark-800 rounded-2xl border border-red-500/20 p-6 text-center">
            <div className="mb-4">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">出现错误</h1>
              <p className="text-gray-300 mb-4">
                编辑器遇到了一个错误，可能是由于存储空间不足或数据损坏导致的。
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleReset}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <RefreshCw size={16} />
                重试
              </button>
              
              <button
                onClick={this.handleClearStorage}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                清空存储数据
              </button>
            </div>

            {this.state.error && (
              <details className="mt-4 text-left">
                <summary className="text-sm text-gray-400 cursor-pointer hover:text-white">
                  查看错误详情
                </summary>
                <div className="mt-2 p-3 bg-dark-700 rounded text-xs text-gray-300 overflow-auto max-h-32">
                  <div className="font-mono">
                    <div className="text-red-400 font-bold">错误信息:</div>
                    <div className="mb-2">{this.state.error.toString()}</div>
                    
                    <div className="text-red-400 font-bold">错误堆栈:</div>
                    <div className="whitespace-pre-wrap">
                      {this.state.errorInfo?.componentStack}
                    </div>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
